import Button from 'components/Button';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FieldDivider from 'components/Form/FieldDivider';
import {FileUploadContainer} from 'components/Form/FileUpload/base';
import Stacked from 'components/Layout/Stacked';
import Link from 'components/Link';
import Progress, {useProgressBar} from 'components/Loaders/Progress';
import Notification from 'components/Notification';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {FC} from 'react';
import {useFormContext} from 'react-hook-form';
import {c, jt, t} from 'ttag';

import {DXInstructionsInfo} from '../../types';
import {sendingSettingsLinks} from '../../utils';
import {UploadProgress} from './utils';

type DXInstructionFileInfoProps = {
    readonly: boolean;
};

const useDxFormContext = () =>
    useFormContext<{instructions: DXInstructionsInfo | null}>();

const DXInstructionFileInfo: FC<DXInstructionFileInfoProps> = ({readonly}) => {
    const {register, setValue, watch} = useDxFormContext();
    const watchInstructions = watch('instructions');

    const removeValue = () => {
        setValue('instructions', null, {shouldDirty: true});
    };

    if (watchInstructions === null) {
        // we shouldn't get here if the DXInstructions is doing the filtering properly, but since
        // the type can be null, this saves throwing an exception / etc. if it's accidentally still
        // rendered.
        return <></>;
    }

    return (
        <Field label={c('dx-helper').t`Current PDF`} type="value">
            <div className="flex flex-wrap gap-2 border-l border-grey-500 pl-4">
                <input type="hidden" {...register('instructions')} />
                <Link
                    className="text-wrap"
                    href={window.Urls.api_dx_partner_settings_instructions_download_view(
                        window.HatsConstants.partner,
                    )}
                    key={watchInstructions.filename}
                    style={{overflowWrap: 'anywhere'}}
                >
                    {watchInstructions.filename}
                </Link>
                <Button
                    disabled={readonly}
                    icon="trash"
                    kind="destructive"
                    name="removeDxInstructions"
                    onClick={removeValue}
                    size="xs"
                >
                    {c('dx-helper').t`Delete File`}
                </Button>
            </div>
        </Field>
    );
};

const DXInstructionFileRemoved = () => (
    <Field label={c('dx-helper').t`Current PDF`} type="value">
        <div className="flex flex-wrap gap-2 border-l border-grey-500">
            <span className="pl-4 pr-2 text-light-secondary">
                {c('dx-helper')
                    .t`File will be ”deleted” only after you click save or you can select a new file below`}
            </span>
        </div>
    </Field>
);

const dxHelperHelpCenterLink = (
    <Link
        href={sendingSettingsLinks.dxHelperHelpCenterLink}
        isOpenInNewTab={true}
        key="help-center"
    >{t`Help Center`}</Link>
);

export type DXInstructionsProps = {
    instructionsFile: File | null;
    instructionsProgress: null | UploadProgress;
    readonly: boolean;
    setInstructionsFile: (value: File | null) => void;
    setInstructionsProgress: (progress: null | UploadProgress) => void;
};

type DXInstructionsProgressProps = {
    dismissError?: () => void;
    progress: UploadProgress;
};

const DXInstructionsProgress: FC<DXInstructionsProgressProps> = ({
    dismissError,
    progress,
}) => {
    if (progress.state === 'error') {
        return (
            <Notification
                icon="danger"
                key="dx-instructions-upload-failed"
                onClick={dismissError}
                type="danger"
            >
                {progress.message}
            </Notification>
        );
    }

    const progressRef = useProgressBar(
        progress.bytesTransferred / progress.bytesTotal,
    );

    if (progress.state === 'done') {
        return (
            <Notification
                icon="success"
                key="dx-instructions-upload-success"
                type="success"
            >
                {c('dx-helper').t`${progress.filename} uploaded successfully.`}
            </Notification>
        );
    }
    return (
        // need to use a Stacked layout here because the progress injects an extra `&nbsp;` when
        // using SideBySide layout
        <Stacked className="mt-1">
            <Progress ref={progressRef} />
        </Stacked>
    );
};

export const DXInstructions: FC<DXInstructionsProps> = ({
    instructionsFile,
    instructionsProgress,
    readonly,
    setInstructionsFile,
    setInstructionsProgress,
}) => {
    const {formState, watch} = useDxFormContext();
    const watchInstructions = watch('instructions');
    const dismissError = () =>
        setInstructionsProgress && setInstructionsProgress(null);
    return (
        <>
            <FieldDivider>
                {t`Email Registration Instructions`}
                <TooltipIcon
                    aria-label="simple-field-tooltip"
                    href={`tooltip-link-TBD`}
                    message={t`DX Instructions Tooltip`}
                    size="base"
                />
            </FieldDivider>
            {watchInstructions && <DXInstructionFileInfo readonly={readonly} />}
            {watchInstructions === null &&
                formState.dirtyFields.instructions && (
                    <DXInstructionFileRemoved />
                )}
            <Field
                helpText={jt`PDF requirements: page size A4. max 3 pages, max file size 10MB. For details, see the ${dxHelperHelpCenterLink} (Japanese site).`}
                label={c('dx-helper').t`Instructions PDF`}
                markOptional={true}
                type="value"
            >
                {instructionsProgress && (
                    <DXInstructionsProgress
                        dismissError={dismissError}
                        progress={instructionsProgress}
                    />
                )}
                {!instructionsProgress && (
                    <FileUploadContainer
                        accept="application/pdf"
                        disabled={readonly}
                        name="dxInstructionsFile"
                        onChange={(objs: File[] | null) => {
                            if (objs !== null && objs.length > 0) {
                                return setInstructionsFile(objs[0]);
                            }
                        }}
                        onReset={() => setInstructionsFile(null)}
                        value={instructionsFile}
                    />
                )}
            </Field>
            <Field
                label={t`Include Instructions in the Envelope`}
                tooltip={{
                    href: 'https://example.org',
                    message: t`Even if not included in the envelope, Clients will be able to download only from registration screens or emails.`,
                }}
                type="value"
            >
                <Checkbox name="attachInstructions" readOnly={readonly} />
            </Field>
        </>
    );
};

export default DXInstructions;
